export type TopIntent = "new" | "existing" | "partner";

export type NewFirstAction =
  | "find_buyer"
  | "find_seller"
  | "find_logistics"
  | "start_compliance"
  | "secure_transaction";

export type SellingWhat = "commodity_lot" | "certified_goods" | "logistics_capacity" | "storage_capacity";
export type BuyingWhat = "agri_commodities" | "verified_eudr_goods" | "storage_services" | "logistics_services";
export type LogisticsMode = "truck" | "rail" | "sea" | "multimodal" | "customs";
export type ComplianceProcess =
  | "dds"
  | "supplier_verification"
  | "shipment_traceability"
  | "audit_preparation"
  | "full_workflow";
export type SecureWhat = "payment" | "contract" | "delivery" | "full_transaction" | "trade_finance";

export type ExistingProblem =
  | "missing_docs"
  | "pending_payment"
  | "shipment_delay"
  | "contract_verification"
  | "customs_issue"
  | "dispute"
  | "need_logistics_partner"
  | "audit_request";

export type PartnerNetwork = "buyer" | "seller" | "broker" | "logistics" | "financial";

export type WorkflowAnswers =
  | {
      topIntent: "new";
      action: NewFirstAction;
      // find buyer
      sellingWhat?: SellingWhat;
      eudrRelevant?: "yes" | "no";
      docsStatus?: "full_dds" | "partial" | "need_support";
      paymentProtection?: "yes" | "no";
      // find seller
      buyingWhat?: BuyingWhat;
      euImportInvolved?: "yes" | "no";
      supplierVerification?: "mandatory" | "preferred" | "optional";
      handling?: "escrow" | "compliance_only" | "standard";
      // logistics
      logisticsMode?: LogisticsMode;
      traceabilityRequired?: "yes" | "no";
      shipmentVerification?: "full" | "basic" | "recommend";
      linkedToExistingTrade?: "yes" | "no";
      // compliance
      complianceProcess?: ComplianceProcess;
      supplierDocsAvailability?: "full" | "partial" | "none";
      externalVerification?: "satellite" | "document_validation" | "risk_scoring" | "full";
      // secure transaction
      secureWhat?: SecureWhat;
      partiesIdentified?: "yes" | "no";
      needComplianceToo?: "yes" | "no";
    }
  | {
      topIntent: "existing";
      problem: ExistingProblem;
      clarifier?: string;
    }
  | {
      topIntent: "partner";
      network: PartnerNetwork;
      offerComplianceServices: "yes" | "no";
    };

export type WorkflowOutcome =
  | { kind: "start_matching"; labelKey: string }
  | { kind: "start_compliance"; labelKey: string }
  | { kind: "activate_escrow"; labelKey: string }
  | { kind: "start_resolution"; labelKey: string }
  | { kind: "start_onboarding"; labelKey: string };

export function computeOutcome(a: WorkflowAnswers | null): WorkflowOutcome | null {
  if (!a) return null;
  if (a.topIntent === "partner") {
    return { kind: "start_onboarding", labelKey: "workflow.outcomes.partnerOnboarding" };
  }
  if (a.topIntent === "existing") {
    return { kind: "start_resolution", labelKey: "workflow.outcomes.caseResolution" };
  }

  // topIntent === "new"
  switch (a.action) {
    case "find_buyer":
      if (a.eudrRelevant === "yes" && a.docsStatus === "need_support") {
        return { kind: "start_compliance", labelKey: "workflow.outcomes.complianceSupport" };
      }
      if (a.paymentProtection === "yes") {
        return { kind: "activate_escrow", labelKey: "workflow.outcomes.escrowActivated" };
      }
      return { kind: "start_matching", labelKey: "workflow.outcomes.buyerMatching" };
    case "find_seller":
      if (a.handling === "escrow") return { kind: "activate_escrow", labelKey: "workflow.outcomes.escrowActivated" };
      if (a.handling === "compliance_only") return { kind: "start_compliance", labelKey: "workflow.outcomes.complianceLaunched" };
      return { kind: "start_matching", labelKey: "workflow.outcomes.verifiedSupplierMatching" };
    case "find_logistics":
      if (a.linkedToExistingTrade === "yes") return { kind: "activate_escrow", labelKey: "workflow.outcomes.secureTransactionLayer" };
      return { kind: "start_matching", labelKey: "workflow.outcomes.logisticsMatching" };
    case "start_compliance":
      return { kind: "start_compliance", labelKey: "workflow.outcomes.complianceLaunched" };
    case "secure_transaction":
      if (a.needComplianceToo === "yes") return { kind: "start_compliance", labelKey: "workflow.outcomes.complianceLaunched" };
      return { kind: "activate_escrow", labelKey: "workflow.outcomes.escrowActivated" };
  }
}

